import { z } from 'zod';
import { ROLES } from './roles.js';
import { ALL_PERMISSION_CODES } from './permissions.js';
import { ALL_MODULE_CODES } from './modules.js';
import { CALL_STATUSES, OFFERED_DAMAGE_AREAS, DAMAGE_CLAIMS, SURVEY_OUTCOMES, SURVEY_OUTCOMES_WITH_CLAIM, } from './tracker-fdfc-follow-up.js';
export const loginSchema = z.object({
    email: z.string().email('Invalid email'),
    password: z.string().min(1, 'Password is required'),
});
export const refreshTokenSchema = z.object({
    refreshToken: z.string().min(1, 'Refresh token is required'),
});
export const changePasswordSchema = z.object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: z
        .string()
        .min(8, 'Password must be at least 8 characters')
        .max(72, 'Password must be at most 72 characters'),
});
export const setStaffPasswordSchema = z.object({
    new_password: z
        .string()
        .min(8, 'Password must be at least 8 characters')
        .max(72, 'Password must be at most 72 characters'),
});
export const updateProfileSchema = z.object({
    first_name: z.string().min(1).max(100).optional(),
    last_name: z.string().min(1).max(100).optional(),
    phone: z.string().max(20).nullable().optional(),
});
export const paginationQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(20),
    sort: z.string().optional(),
    q: z.string().optional(),
});
// ---- FDFC Tracker (webhook) ----
export const trackerFdfcListQuerySchema = paginationQuerySchema.extend({
    course_level: z.string().trim().min(1).optional(),
    dispatch_status: z
        .enum(['not_dispatched', 'dispatching', 'dispatched', 'failed', 'unknown'])
        .optional(),
    call_status: z.enum(CALL_STATUSES).optional(),
    /** `none` = no survey answer yet. */
    survey_outcome: z.enum([...SURVEY_OUTCOMES, 'none']).optional(),
});
/** Blank text is no text: "" and "   " are stored as null. */
const optionalText = (max) => z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => (v ? v : null));
/**
 * A call: a new status, a remark, or both. Leave `status` out to add a remark
 * against the status as it stands (the server keeps the stored value).
 */
export const trackerFdfcCallLogSchema = z
    .object({
    status: z.enum(CALL_STATUSES).optional(),
    remark: optionalText(1000),
})
    .refine((v) => v.status || v.remark, { message: 'Choose a status or write a remark', path: ['remark'] });
/** The recipient (name + courier address) as corrected by the team. Limits match the columns. */
export const trackerFdfcAddressSchema = z
    .object({
    full_name: optionalText(150),
    address_line1: optionalText(255),
    address_line2: optionalText(255),
    city: optionalText(100),
    state: optionalText(100),
    // "395 004" is the same PIN as "395004".
    pincode: optionalText(20).transform((v) => (v ? v.replace(/\s+/g, '') : v)),
    country: optionalText(100),
    alt_phone: optionalText(20),
})
    .refine((v) => Object.values(v).some(Boolean), {
    message: 'Fill in at least one field',
    path: ['address_line1'],
});
/**
 * What the person said about the kit. A damaged / missing kit may carry the
 * decision on it; only a re-dispatch needs the list of what goes in the box,
 * and "Something else" needs the thing typed out.
 */
export const trackerFdfcSurveySchema = z
    .object({
    outcome: z.enum(SURVEY_OUTCOMES),
    damage_claim: z.enum(DAMAGE_CLAIMS).nullish().transform((v) => v ?? null),
    damage_areas: z
        .array(z.enum(OFFERED_DAMAGE_AREAS))
        .max(OFFERED_DAMAGE_AREAS.length)
        .nullish()
        .transform((v) => (v?.length ? [...new Set(v)] : null)),
    damage_detail: optionalText(300),
    notes: optionalText(1000),
})
    .superRefine((v, ctx) => {
    if (!SURVEY_OUTCOMES_WITH_CLAIM.includes(v.outcome) && (v.damage_claim || v.damage_areas)) {
        ctx.addIssue({ code: 'custom', path: ['damage_claim'], message: 'Only a damaged or missing kit takes a decision' });
    }
    if (v.damage_claim === 'redispatch' && !v.damage_areas) {
        ctx.addIssue({ code: 'custom', path: ['damage_areas'], message: 'Tick what goes in the new box' });
    }
    if (v.damage_claim === 'redispatch' && v.damage_areas?.includes('other') && !v.damage_detail) {
        ctx.addIssue({ code: 'custom', path: ['damage_detail'], message: 'Say what the other item is' });
    }
})
    // Items belong to a re-dispatch only, and the typed item to "other" only.
    .transform((v) => {
    const areas = v.damage_claim === 'redispatch' ? v.damage_areas : null;
    return {
        ...v,
        damage_areas: areas,
        damage_detail: areas?.includes('other') ? v.damage_detail : null,
    };
});
/** Most entries one Dispatch click may send; each is a real order at NSDC. */
export const TRACKER_FDFC_DISPATCH_MAX = 50;
export const trackerFdfcDispatchSchema = z.object({
    ids: z.array(z.string().uuid()).min(1, 'Select at least one entry').max(TRACKER_FDFC_DISPATCH_MAX, `Select ${TRACKER_FDFC_DISPATCH_MAX} entries or fewer`),
    kit_code: z.string().trim().regex(/^KIT-\d{4}-\d{6}$/i, 'Choose a kit'),
});
/** "Dispatch another order": one more kit order for an entry that already has one. */
export const trackerFdfcDispatchAgainSchema = z.object({
    kit_code: z.string().trim().regex(/^KIT-\d{4}-\d{6}$/i, 'Choose a kit'),
});
// ---- Staff (users) ----
const roleCodeSchema = z.enum([
    ROLES.SUPER_ADMIN,
    ROLES.ADMIN,
    ROLES.MANAGER,
    ROLES.TEAM_LEADER,
    ROLES.USER,
]);
const moduleCodeSchema = z.enum(ALL_MODULE_CODES);
const permissionCodeSchema = z.enum(ALL_PERMISSION_CODES);
export const createStaffSchema = z.object({
    email: z.string().email(),
    password: z.string().min(8).max(72),
    employee_code: z.string().min(1).max(50),
    first_name: z.string().min(1).max(100),
    last_name: z.string().min(1).max(100),
    phone: z.string().min(1, 'Phone is required').max(20),
    role_codes: z.array(roleCodeSchema).min(1, 'At least one role required'),
    module_overrides: z
        .object({
        include: z.array(moduleCodeSchema).optional(),
        exclude: z.array(moduleCodeSchema).optional(),
    })
        .optional(),
});
export const updateStaffSchema = z.object({
    email: z.string().email().optional(),
    employee_code: z.string().min(1).max(50).optional(),
    first_name: z.string().min(1).max(100).optional(),
    last_name: z.string().min(1).max(100).optional(),
    phone: z.string().max(20).nullable().optional(),
    is_active: z.boolean().optional(),
});
export const listStaffQuerySchema = paginationQuerySchema.extend({
    role: roleCodeSchema.optional(),
    is_active: z
        .enum(['true', 'false'])
        .optional()
        .transform((v) => (v === undefined ? undefined : v === 'true')),
});
export const assignRolesSchema = z.object({
    role_codes: z.array(roleCodeSchema).min(1, 'At least one role required'),
});
export const assignModulesSchema = z.object({
    add: z.array(moduleCodeSchema).optional(),
    remove: z.array(moduleCodeSchema).optional(),
});
export const setRolePermissionsSchema = z.object({
    permission_codes: z.array(permissionCodeSchema),
});
export const setRoleDefaultModulesSchema = z.object({
    module_codes: z.array(moduleCodeSchema),
});
//# sourceMappingURL=zod-schemas.js.map